import { JsonPatch } from "json8-patch";
import streamSaver from "streamsaver";
import { WritableStream } from "web-streams-polyfill/ponyfill";
import { AxiosError } from "axios";

import { http, HttpConfig, UploadDownloadProgressInterface } from "../http/HttpService";
import { registerGlobalService } from "../service-provider/ServiceRegistry";
import ApiErrorFactory from "./ApiErrorFactory";
import { configuration } from "../configuration/ConfigurationService";
import { authentication } from "../authentication/Authentication";
import { globalModule } from "../service-provider/GlobalModule";
import { PubSubEvents } from "../PubSubEvents";
import { Endpoint } from "./ApiEndpoints";
import { organizationSubscriptionService } from "../organization/OrganizationSubscription";
import { appSessionStorage } from "../storage/SessionStorageService";
import { OrganizationKey } from "../organization/LastAccessedOrganizationStorage";

streamSaver.mitm = "/mitm.html";

export interface Api {
  get: (endpoint: Endpoint) => Promise<any>;
  post: (endpoint: Endpoint, data: any, config?: HttpConfig) => Promise<any>;
  put: (endpoint: Endpoint, data?: any) => Promise<any>;
  upload: (
    endpoint: Endpoint,
    file: FormData,
    onUploadProgress?: UploadDownloadProgressInterface
  ) => Promise<any>;
  delete: (endpoint: Endpoint, data?: any, config?: any) => Promise<any>;
  patch: (endpoint: Endpoint, data: PatchData) => Promise<any>;
  download: (
    endpoint: Endpoint,
    onDownloadProgres?: UploadDownloadProgressInterface
  ) => Promise<any>;
  getDownloadToDisk: (endpoint: Endpoint, fileName: string) => Promise<any>;
  postDownloadToDisk: (endpoint: Endpoint, fileName: string, body: any) => Promise<any>;
}

export interface singleItemPatch {
  op: string;
  path: string;
  value: number | object;
  from?: string;
}

export interface multipleItemPatch {
  ids: string[];
  [key: string]: singleItemPatch[] | string[];
}

export type PatchData = singleItemPatch[] | multipleItemPatch | JsonPatch;

export class ApiService implements Api {
  get = async (endpoint: Endpoint) => {
    let configurationContent = await configuration.get();

    try {
      const request = await this.getRequest(
        endpoint,
        configurationContent,
        "application/json; charset=utf-8; application/problem+json"
      );

      const endpointUrl = this.getEndpointUrl(endpoint);
      let response = await http.get(configurationContent.api + endpointUrl, request);
      return response.data;
    } catch (err) {
      throw ApiErrorFactory.create(err as AxiosError<any>);
    }
  };

  getAsync = async (endpoint: Endpoint) => {
    let configurationContent = await configuration.get();

    try {
      const request = await this.getRequest(
        endpoint,
        configurationContent,
        "application/json; charset=utf-8; application/problem+json"
      );

      const endpointUrl = this.getEndpointUrl(endpoint);
      http.get(configurationContent.api + endpointUrl, request);
      return;
    } catch (err) {
      throw ApiErrorFactory.create(err as AxiosError<any>);
    }
  };

  post = async (endpoint: Endpoint, data: any, config?: HttpConfig | undefined) => {
    let configurationContent = await configuration.get();

    const request = await this.getRequest(
      endpoint,
      configurationContent,
      "application/json; charset=utf-8; application/problem+json"
    );

    const requestWithConfig = { ...request, ...config };

    try {
      const endpointUrl = this.getEndpointUrl(endpoint);
      return await http.post(configurationContent.api + endpointUrl, data, requestWithConfig);
    } catch (err) {
      throw ApiErrorFactory.create(err as AxiosError<any>);
    }
  };

  put = async (endpoint: Endpoint, data?: any, config?: HttpConfig) => {
    let configurationContent = await configuration.get();

    const request = await this.getRequest(
      endpoint,
      configurationContent,
      "application/json; charset=utf-8; application/problem+json"
    );

    const requestWithConfig = { ...request, ...config };

    try {
      const endpointUrl = this.getEndpointUrl(endpoint);
      return await http.put(configurationContent.api + endpointUrl, data, requestWithConfig);
    } catch (err) {
      throw ApiErrorFactory.create(err as AxiosError<any>);
    }
  };

  upload = async (
    endpoint: Endpoint,
    file: FormData,
    onUploadProgress?: UploadDownloadProgressInterface | undefined
  ) => {
    return await this.put(endpoint, file, { onUploadProgress });
  };

  delete = async (endpoint: Endpoint, data?: any, config?: any) => {
    let configurationContent = await configuration.get();

    const request = await this.getRequest(
      endpoint,
      configurationContent,
      "application/json; charset=utf-8; application/problem+json"
    );

    const requestWithConfigAndData = { ...request, ...config, data };

    try {
      const endpointUrl = this.getEndpointUrl(endpoint);
      return await http.delete(configurationContent.api + endpointUrl, requestWithConfigAndData);
    } catch (err) {
      throw ApiErrorFactory.create(err as AxiosError<any>);
    }
  };

  patch = async (endpoint: Endpoint, data: PatchData) => {
    try {
      let configurationContent = await configuration.get();
      const request = await this.getRequest(
        endpoint,
        configurationContent,
        "application/json; charset=utf-8; application/problem+json"
      );

      const endpointUrl = this.getEndpointUrl(endpoint);
      return await http.patch(configurationContent.api + endpointUrl, data, request);
    } catch (err) {
      throw ApiErrorFactory.create(err as AxiosError<any>);
    }
  };

  download = async (
    endpoint: Endpoint,
    onDownloadProgress?: UploadDownloadProgressInterface | undefined
  ) => {
    try {
      let configurationContent = await configuration.get();
      const request = await this.getRequest(
        endpoint,
        configurationContent,
        "application/octet-stream;"
      );

      const endpointUrl = this.getEndpointUrl(endpoint);
      return await http.get(configurationContent.api + endpointUrl, {
        ...request,
        responseType: "blob",
        onDownloadProgress,
      });
    } catch (err) {
      throw ApiErrorFactory.create(err as AxiosError<any>);
    }
  };

  getDownloadToDisk = async (endpoint: Endpoint, fileName: string) => {
    let configurationContent = await configuration.get();
    const endpointUrl = this.getEndpointUrl(endpoint);

    const request = await this.getRequest(endpoint, configurationContent, "application/json");

    try {
      const response: Response = await fetch(configurationContent.api + endpointUrl, {
        method: "GET",
        credentials: "include",
        ...request,
      });

      if (!response.ok) {
        throw new Error(`${response.status}`);
      }

      await this.streamResponseToUserBrowser(response, fileName, endpoint.url);
    } catch (error) {
      console.log(error);
      throw ApiErrorFactory.create(error as AxiosError<any>);
    }
  };

  postDownloadToDisk = async (endpoint: Endpoint, fileName: string, body: any) => {
    let configurationContent = await configuration.get();
    const endpointUrl = this.getEndpointUrl(endpoint);

    const request = await this.getRequest(endpoint, configurationContent, "application/json");

    try {
      const response: Response = await fetch(configurationContent.api + endpointUrl, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify(body),
        ...request,
        
      });

      if (!response.ok) {
        throw new Error(`${response.status}`);
      }

      await this.streamResponseToUserBrowser(response, fileName, endpoint.url);
    } catch (error) {
      console.log(error);
      throw ApiErrorFactory.create(error as AxiosError<any>);
    }
  };

  private async getRequest(endpoint: Endpoint, configurationContent: any, contentType: string) {
    const accessToken = await authentication.getAccessToken();
    const organizationId = appSessionStorage.getStorageItem(OrganizationKey);

    const organizationSubscriptionServiceKey = organizationSubscriptionService.data;
    return {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": contentType,
        OrganizationId: organizationId || "",
        "Ocp-Apim-Subscription-Key": endpoint.includeOrganizationSubscription
          ? organizationSubscriptionServiceKey
          : configurationContent.subscription_key,
      },
    };
  }

  private getEndpointUrl(endpoint: Endpoint) {
    let parsedEnpoint = endpoint;
    endpoint.routeParams?.forEach((element, index) => {
      parsedEnpoint.url = endpoint.url.replace("{" + index + "}", element.toString());
    });

    if (endpoint.queryParams) parsedEnpoint.url = `${parsedEnpoint.url}?${endpoint.queryParams}`;

    return parsedEnpoint.url;
  }

  private ponyfillWritableStreamIfNeeded() {
    // If the WritableStream is not available (Firefox, Safari), take it from the ponyfill
    if (!window.WritableStream) {
      (streamSaver as any).WritableStream = WritableStream;
      (window as any).WritableStream = WritableStream;
    }
  }

  private sendEventThatDownloadIsVisibleInBrowserBar(url: string) {
    PubSub.publish(`${PubSubEvents.DownloadingItemIsVisibleInBrowser}-${url}`);
  }

  private readableStreamCanBePiped(readableStream: ReadableStream<Uint8Array> | null) {
    return readableStream && readableStream.pipeTo;
  }

  private async manuallyReadFromResponseAndWriteToWindow(
    response: Response,
    fileStream: globalThis.WritableStream<any>
  ) {
    const writer = fileStream.getWriter();
    const reader = response.body?.getReader();

    const pump = async () => {
      const readResult = await reader?.read();
      if (readResult?.done) await writer.close();
      else {
        await writer?.write(readResult?.value);
        await pump();
      }
    };

    await pump();
  }

  private async abortWhenClosingPage(fileStream: globalThis.WritableStream<any>) {
    window.onunload = async () => {
      await fileStream.abort();
    };
  }

  private async streamResponseToUserBrowser(response: Response, fileName: string, url: string) {
    this.ponyfillWritableStreamIfNeeded();

    this.sendEventThatDownloadIsVisibleInBrowserBar(url);

    const fileSize = Number(response.headers.get("filesize"));
    const fileStream = streamSaver.createWriteStream(fileName, { size: fileSize });
    const readableStream = response.body;

    if (this.readableStreamCanBePiped(readableStream)) await readableStream?.pipeTo(fileStream);
    else await this.manuallyReadFromResponseAndWriteToWindow(response, fileStream);

    this.abortWhenClosingPage(fileStream);
  }
}

export const api = new ApiService();
globalModule.api = api;
export default registerGlobalService(api as Api);
